import type { TerrainType } from "../constants";

export const STORY_BIOMES = [
  "dark_forest_entry",
  "natural_path",
  "bright_forest_breathing_area",
  "ancient_ruins",
  "dense_forest_corridor",
  "corrupted_pressure_zone",
  "rocky_open_area",
  "final_spirit_chase",
  "pack_call_exit"
] as const;

export type StoryBiomeId = (typeof STORY_BIOMES)[number];

export interface StoryBiomeProfile {
  id: StoryBiomeId;
  label: string;
  pathPrimary: TerrainType;
  pathSecondary: TerrainType;
  pathWidth: number;
  pathHeight: number;
  pathAlpha: number;
  obstacleBonus: number;
  spiritMultiplier: number;
  pickupChanceOffset: number;
  landmarkChance: number;
}

export const STORY_CONFIG = {
  core: {
    protagonist: "white_wolf",
    goal: "return_to_pack",
    conflict: "forbidden_forest_spirits",
    innerForce: "champion"
  },
  songDurationSeconds: 120,
  mapProgression: STORY_BIOMES,
  biomeProfiles: {
    dark_forest_entry: {
      id: "dark_forest_entry",
      label: "Borde oscuro",
      pathPrimary: "dirt",
      pathSecondary: "roots",
      pathWidth: 230,
      pathHeight: 210,
      pathAlpha: 0.18,
      obstacleBonus: 1,
      spiritMultiplier: 1.08,
      pickupChanceOffset: 0.04,
      landmarkChance: 1
    },
    natural_path: {
      id: "natural_path",
      label: "Sendero natural",
      pathPrimary: "dirt",
      pathSecondary: "grass",
      pathWidth: 245,
      pathHeight: 220,
      pathAlpha: 0.17,
      obstacleBonus: 0,
      spiritMultiplier: 0.78,
      pickupChanceOffset: 0.02,
      landmarkChance: 0.32
    },
    bright_forest_breathing_area: {
      id: "bright_forest_breathing_area",
      label: "Claro vivo",
      pathPrimary: "grass",
      pathSecondary: "dirt",
      pathWidth: 270,
      pathHeight: 235,
      pathAlpha: 0.15,
      obstacleBonus: -1,
      spiritMultiplier: 0.42,
      pickupChanceOffset: 0.04,
      landmarkChance: 0.62
    },
    ancient_ruins: {
      id: "ancient_ruins",
      label: "Ruinas antiguas",
      pathPrimary: "stone",
      pathSecondary: "dirt",
      pathWidth: 232,
      pathHeight: 190,
      pathAlpha: 0.24,
      obstacleBonus: 1,
      spiritMultiplier: 0.92,
      pickupChanceOffset: -0.02,
      landmarkChance: 0.86
    },
    dense_forest_corridor: {
      id: "dense_forest_corridor",
      label: "Pasillo denso",
      pathPrimary: "roots",
      pathSecondary: "dirt",
      pathWidth: 218,
      pathHeight: 205,
      pathAlpha: 0.2,
      obstacleBonus: 2,
      spiritMultiplier: 1.06,
      pickupChanceOffset: -0.04,
      landmarkChance: 0.58
    },
    corrupted_pressure_zone: {
      id: "corrupted_pressure_zone",
      label: "Zona corrupta",
      pathPrimary: "mud",
      pathSecondary: "swamp",
      pathWidth: 225,
      pathHeight: 205,
      pathAlpha: 0.16,
      obstacleBonus: 1,
      spiritMultiplier: 1.24,
      pickupChanceOffset: -0.06,
      landmarkChance: 0.72
    },
    rocky_open_area: {
      id: "rocky_open_area",
      label: "Apertura rocosa",
      pathPrimary: "stone",
      pathSecondary: "dirt",
      pathWidth: 286,
      pathHeight: 230,
      pathAlpha: 0.2,
      obstacleBonus: -1,
      spiritMultiplier: 0.86,
      pickupChanceOffset: -0.02,
      landmarkChance: 0.54
    },
    final_spirit_chase: {
      id: "final_spirit_chase",
      label: "Persecucion final",
      pathPrimary: "roots",
      pathSecondary: "stone",
      pathWidth: 226,
      pathHeight: 188,
      pathAlpha: 0.22,
      obstacleBonus: 2,
      spiritMultiplier: 1.35,
      pickupChanceOffset: -0.08,
      landmarkChance: 0.68
    },
    pack_call_exit: {
      id: "pack_call_exit",
      label: "Llamado de manada",
      pathPrimary: "grass",
      pathSecondary: "stone",
      pathWidth: 292,
      pathHeight: 242,
      pathAlpha: 0.19,
      obstacleBonus: -2,
      spiritMultiplier: 0.34,
      pickupChanceOffset: 0.03,
      landmarkChance: 0.9
    }
  } satisfies Record<StoryBiomeId, StoryBiomeProfile>,
  spiritPressure: {
    enabled: true,
    maxDistancePx: 900,
    minOpacity: 0.04,
    maxOpacity: 0.28,
    maxEyeCount: 8,
    edgeWidth: 54,
    bottomHeight: 180,
    eyeAlpha: 0.34
  },
  championPulse: {
    enabled: true,
    triggerOnSprint: true,
    triggerOnFinalChorus: true,
    maxIntensity: 0.42,
    ringRadius: 86,
    avoidStrongFlashes: true
  },
  storyBeats: {
    biomeFlashSeconds: 0.75,
    championSurgeSeconds: 0.55,
    minSecondsBetweenBeats: 3.2,
    hints: {
      dark_forest_entry: "Entraste al bosque prohibido",
      natural_path: "Corre hacia el norte",
      bright_forest_breathing_area: "La manada sigue lejos",
      ancient_ruins: "Este lugar tenia guardianes",
      dense_forest_corridor: "El bosque cierra el paso",
      corrupted_pressure_zone: "Algo te vigila",
      rocky_open_area: "No te detengas",
      final_spirit_chase: "Campeon despierta dentro",
      pack_call_exit: "Vuelve con tu manada"
    } satisfies Record<StoryBiomeId, string>
  }
} as const;

export function getStoryBiomeProfile(id: StoryBiomeId): StoryBiomeProfile {
  return STORY_CONFIG.biomeProfiles[id];
}
